import { useEffect, useState } from 'react';
import { apiFetch } from '../apiBase';
import CountUp from '../components/CountUp';

const SITES = [
  { id: 'casesgg', logo: '/images/casesgg-logo.png' },
  { id: 'winovo', logo: '/images/winovo-logo.png' },
];

// Everything here that differs from one live leaderboard to the next — the
// rest (prize tiers, total pool, end date) comes from the API response
// itself so it's never stale. `accent` is sampled from the site's logo; the
// whole page recolours to it while that site's tab is open.
const PLATFORM_CONFIG = {
  casesgg: {
    title: 'CASES.GG',
    accent: '#5aa9ff',
    logo: '/images/casesgg-logo.png',
    code: 'FREEMAN',
    claimUrl: 'https://cases.gg/r/FREEMAN',
    apiPath: '/api/leaderboards/casesgg',
    period: 'Bi-Weekly',
    valuePrefix: '$',
    valueDecimals: 2,
    valueLabel: 'Wagered',
  },
  winovo: {
    title: 'WINOVO',
    accent: '#ebbe7b',
    logo: '/images/winovo-logo.png',
    code: 'FREEMAN',
    claimUrl: 'https://winovo.io/?ref=FreeMan',
    apiPath: '/api/leaderboards/winovo',
    period: 'Weekly',
    valuePrefix: '$',
    valueDecimals: 2,
    valueLabel: 'Wagered',
    note: "Players must deposit more than their placement prize — if the deposit is less than the prize, that prize is voided.",
  },
};

const AK47_ASSET =
  'https://lh3.googleusercontent.com/aida-public/AB6AXuAb3beTqQypVjfVTnDQWmy5-dI-FXNZSkQ5_7kG8-JEv0F6PfBc1vtEmA4G-YjPe8UDYnfmOqUhFHozHXUpfGTxGOvgQ3ZwFu3dxoSovv48hQbM-N1KubvK88jlVwNbwGJB19KEcGcvGuOMM0Id5Rr2CLZFoyWt7AdOeKn5I-htnuOGUi1d06gZnppgX_HSfKtZnMyElZ3Gni4kmzE6c2NgnHr2QaRp88jF-LLwjWKtX06TJpU--efxla_nFZa9ZBMrEA';
const LOCK_ASSET =
  'https://lh3.googleusercontent.com/aida-public/AB6AXuAg6i4Q2pK0ynV4lhB_b11udEfQ_k8k-VWWQXnS7FkEgEz8rZGpMvXlts_C_u2Xs-KdJvyKSbuv2WwoIaAtQMazvU7STZDwLBtgWCaEKLc3UuLM2-byKQDMTLJJjHaBhjEBL_7u4mwzMhKje2mQ0DxdNqB1bBDRpkNhl-naSbWRikvGGwWUyAD306Ef0yUo98_3Us_EJPXNf89bosP402SiHlkYazLOAiEW7GFNpS4mqhYQuUTDbtGre9wlYJJnjrG-wA';

// Money reads green-dollar / white-digits / dim-cents; prizes are green throughout.
const MONEY_GREEN = '#5ee08a';

// Classic medal metals (gold / silver / bronze) so rank reads at a glance,
// whatever the site accent is. light → base → dark is the plaque's sheen.
const MEDAL = {
  1: { base: '#F6BB42', light: '#FFE39A', dark: '#D48F17', ink: '#1d1405' },
  2: { base: '#C9CDD5', light: '#F5F6F9', dark: '#959BA6', ink: '#17171e' },
  3: { base: '#D08A5A', light: '#F4BC93', dark: '#A0562F', ink: '#1d1009' },
};

const SHOW_OPTIONS = [10, 20, 50];

const fmt = (n, decimals = 2) => n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

// Integer part at full strength, cents dimmed, the currency sign tinted —
// keeps big numbers scannable without the cents competing for attention.
function moneyParts(n, decimals, prefix = '', prefixColor = MONEY_GREEN) {
  const [intPart, decPart] = fmt(n, decimals).split('.');
  return (
    <>
      {prefix && <span style={{ color: prefixColor }}>{prefix}</span>}
      {intPart}
      {decPart != null && <span style={{ opacity: 0.45 }}>.{decPart}</span>}
    </>
  );
}

function Avatar({ row, size }) {
  const [broken, setBroken] = useState(false);
  const box = { width: size, height: size };
  if (row?.avatar && !broken) {
    return <img src={row.avatar} alt={row.name} onError={() => setBroken(true)} className="rounded-full object-cover flex-shrink-0" style={box} />;
  }
  return (
    <div
      className="rounded-full flex items-center justify-center flex-shrink-0 font-display-lg text-white"
      style={{ ...box, background: '#101017', boxShadow: 'inset 0 0 0 1px rgba(255,255,255,.07)', fontSize: size * 0.46 }}
    >
      {row?.name ? row.name.charAt(0).toUpperCase() : '?'}
    </div>
  );
}

function Crown() {
  return (
    <svg width="34" height="26" viewBox="0 0 34 26" className="lb-crown" aria-hidden="true">
      <defs>
        <linearGradient id="lbCrownGold" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#FFE39A" />
          <stop offset="1" stopColor="#D48F17" />
        </linearGradient>
      </defs>
      <path d="M2 8l7.5 6L17 2l7.5 12L32 8l-3 14H5z" fill="url(#lbCrownGold)" stroke="#7a5208" strokeWidth="1" strokeLinejoin="round" />
      <rect x="5" y="21" width="24" height="4" rx="1" fill="url(#lbCrownGold)" stroke="#7a5208" strokeWidth="1" />
    </svg>
  );
}

// Phones keep the three cards side by side (like a real podium) but shrink them.
function useNarrow(query = '(max-width: 639px)') {
  const [narrow, setNarrow] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setNarrow(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);
  return narrow;
}

function PodiumCard({ row, place, prize, cfg, compact }) {
  const m = MEDAL[place];
  const first = place === 1;
  const sz = (big, small) => (compact ? small : big);
  const avatar = first ? sz(76, 50) : sz(66, 44);
  return (
    <div className={`relative flex flex-col items-center ${first ? (compact ? '-mt-5' : '-mt-8') : ''}`}>
      {first && <Crown />}
      <div className="lb-card-edge w-full">
        <div
          className={`lb-card flex flex-col items-center text-center ${compact ? 'px-1.5' : 'px-3'}`}
          style={{ paddingTop: first ? sz(30, 18) : sz(24, 14), minHeight: first ? sz(262, 196) : sz(238, 178) }}
        >
          <div className="relative">
            <div className="rounded-full p-1" style={{ background: 'rgba(255,255,255,.04)', boxShadow: '0 0 0 1px rgba(255,255,255,.06)' }}>
              <Avatar row={row} size={avatar} />
            </div>
            <div
              className="lb-hex absolute left-1/2 -translate-x-1/2 flex items-center justify-center font-display-lg"
              style={{ bottom: -11, width: 22, height: 24, fontSize: 12, color: m.ink, background: `linear-gradient(180deg, ${m.light}, ${m.base} 55%, ${m.dark})` }}
            >
              {place}
            </div>
          </div>
          <div className={`${compact ? 'mt-4 h-5' : 'mt-6 h-7'} flex items-center justify-center w-full`}>
            {row ? (
              <span className="font-title-md font-bold text-white truncate max-w-full" style={{ fontSize: first ? sz(17, 12) : sz(15, 11) }}>{row.name}</span>
            ) : (
              <span className="lb-skeleton" style={{ width: first ? sz(112, 60) : sz(96, 52) }} />
            )}
          </div>
          <div className={`font-display-lg uppercase text-[#c9c9d6] ${compact ? 'mt-2' : 'mt-3'}`} style={{ fontSize: sz(11, 8.5), letterSpacing: '.06em' }}>{cfg.valueLabel}</div>
          <div className="font-mono-brand font-bold text-white mt-0.5" style={{ fontSize: first ? sz(15, 11) : sz(14, 10.5) }}>
            {row ? (
              <CountUp value={row.wagered} formatter={(n) => moneyParts(n, cfg.valueDecimals, cfg.valuePrefix)} />
            ) : (
              moneyParts(0, cfg.valueDecimals, cfg.valuePrefix)
            )}
          </div>
        </div>
      </div>
      {prize != null && (
        <div className="relative flex flex-col items-center" style={{ marginTop: sz(-30, -22), zIndex: 2 }}>
          <div
            className="lb-plaque font-display-lg"
            style={{
              background: `linear-gradient(180deg, ${m.light}, ${m.base} 52%, ${m.dark})`,
              color: m.ink,
              fontSize: first ? sz(21, 14) : sz(19, 13),
              padding: first ? sz('7px 34px', '4px 12px') : sz('6px 28px', '4px 10px'),
              boxShadow: `0 8px 24px ${m.base}40, inset 0 1px 0 rgba(255,255,255,.55), inset 0 -2px 0 rgba(0,0,0,.18)`,
            }}
          >
            ${Number.isInteger(prize) ? prize.toLocaleString() : fmt(prize, 2)}
          </div>
          <div className="lb-plaque-tab flex items-center justify-center">
            <span className="material-symbols-outlined" style={{ fontSize: 13, color: m.base }}>emoji_events</span>
          </div>
        </div>
      )}
    </div>
  );
}

function RaceCountdown({ startsAt, endsAt, status, accent }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  // Before the start the timer counts down to it; after, down to the end. A
  // race with no end date just runs — no timer.
  const upcoming = status === 'upcoming' && startsAt != null;
  const open = !upcoming && endsAt == null;
  const diff = open ? 0 : Math.max(0, (upcoming ? startsAt : endsAt) - now);
  const isActive = status === 'active' && (open || diff > 0);
  const units = [
    ['Days', Math.floor(diff / 86400000)],
    ['Hrs', Math.floor((diff % 86400000) / 3600000)],
    ['Min', Math.floor((diff % 3600000) / 60000)],
    ['Sec', Math.floor((diff % 60000) / 1000)],
  ];
  return (
    <div className="flex flex-col items-center text-center mt-16 mb-14">
      <div className="lb-flame" style={{ '--lb-accent': accent }}>
        <span className="material-symbols-outlined" style={{ color: accent, fontSize: 22 }}>local_fire_department</span>
      </div>
      <h3 className="font-display-lg uppercase text-white mt-3" style={{ fontSize: 19, letterSpacing: '.02em' }}>
        {upcoming ? 'Leaderboard starts in' : open ? 'Leaderboard is live' : isActive ? 'Leaderboard ends in' : 'Leaderboard has ended'}
      </h3>
      <p className="font-display-lg uppercase text-[#8b8b9c] mt-2 max-w-xs" style={{ fontSize: 10.5, letterSpacing: '.05em', lineHeight: 1.6 }}>
        {upcoming
          ? 'Only wagers placed after the start count — sign up now so you are ready'
          : open
            ? 'Standings move as players wager'
            : isActive
              ? 'The winner will be determined when the time runs out, hurry up!'
              : 'Final standings are locked in'}
      </p>
      {!open && (
        <div className="flex gap-2 mt-6" style={{ opacity: isActive || upcoming ? 1 : 0.5 }}>
          {units.map(([lbl, val]) => (
            <div key={lbl} className="lb-hex lb-hex-edge">
              <div className="lb-hex lb-hex-inner flex flex-col items-center justify-center">
                <span className="font-display-lg leading-none" style={{ fontSize: 15, color: accent }}>{val}</span>
                <span className="font-display-lg uppercase text-[#8b8b9c] mt-1" style={{ fontSize: 8, letterSpacing: '.06em' }}>{lbl}</span>
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="font-display-lg uppercase text-[#5d5d6c] mt-6" style={{ fontSize: 10, letterSpacing: '.06em' }}>
        {isActive ? 'Leaderboard updates every 10 minutes' : upcoming ? 'The board opens when the timer hits zero' : 'Race closed'}
      </p>
    </div>
  );
}

function ChallengerRow({ rank, row, prize, cfg }) {
  return (
    <div
      className={`lb-row grid grid-cols-[26px_1fr_86px_64px] sm:grid-cols-[48px_1fr_160px_120px] items-center gap-2 sm:gap-3 px-3 sm:px-4 rounded-lg ${row ? '' : 'opacity-50'}`}
      style={{ height: 42, background: '#1b1b24', border: '1px solid #22222d' }}
    >
      <div className="font-mono-brand text-[13px] text-[#d6d6e0] text-center">{rank}</div>
      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
        <Avatar row={row} size={24} />
        {row ? (
          <span className="font-title-md font-semibold text-[13px] text-[#c9c9d6] truncate">{row.name}</span>
        ) : (
          <span className="lb-skeleton" style={{ width: 72, height: 8 }} />
        )}
      </div>
      <div className="font-mono-brand font-bold text-[13px] text-white text-right">
        {row ? moneyParts(row.wagered, cfg.valueDecimals, cfg.valuePrefix) : <span className="text-[#5d5d6c]">—</span>}
      </div>
      <div className="font-mono-brand font-bold text-[13px] text-right" style={{ color: MONEY_GREEN }}>
        {prize != null ? moneyParts(prize, 2, '$') : <span className="text-[#5d5d6c]">—</span>}
      </div>
    </div>
  );
}

export default function Leaderboards() {
  const [active, setActive] = useState('casesgg');
  const [show, setShow] = useState(SHOW_OPTIONS[0]);
  const narrow = useNarrow();
  // Keyed by platform id so switching tabs doesn't lose what's already been
  // fetched for the other one — each platform loads once, not on every tab click.
  const [liveData, setLiveData] = useState({});

  useEffect(() => {
    const config = PLATFORM_CONFIG[active];
    if (!config || liveData[active]) return;
    apiFetch(config.apiPath)
      .then((r) => r.json())
      .then((data) => setLiveData((d) => ({ ...d, [active]: { data } })))
      .catch(() => setLiveData((d) => ({ ...d, [active]: { error: 'Could not load the leaderboard right now.' } })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const cfg = PLATFORM_CONFIG[active];
  const accent = cfg.accent;
  const { data: lb, error: liveError } = liveData[active] || {};

  // The race flips state on its own clock (upcoming -> active at the start,
  // active -> ended at the end), so reload the board just after that moment
  // instead of leaving a stale status on screen.
  const nextChange = lb ? (lb.status === 'upcoming' ? lb.startsAt : lb.status === 'active' ? lb.endsAt : null) : null;
  useEffect(() => {
    if (!nextChange) return;
    const t = setTimeout(() => {
      apiFetch(cfg.apiPath)
        .then((r) => r.json())
        .then((data) => setLiveData((d) => ({ ...d, [active]: { data } })))
        .catch(() => {});
    }, Math.min(2 ** 31 - 1, Math.max(1000, nextChange - Date.now() + 3000)));
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextChange, active]);

  const [copied, setCopied] = useState(false);
  const copyCode = () => {
    navigator.clipboard?.writeText(cfg.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const prizeAt = (rank) => (lb?.prizes && lb.prizes[rank - 1] != null ? lb.prizes[rank - 1] : null);
  const rowAt = (rank) => lb?.rows.find((r) => r.rank === rank);

  return (
    <main className="flex-grow pt-12 pb-16 px-5 max-w-[1100px] mx-auto w-full relative z-10" style={{ '--lb-accent': accent }}>
      {/* Re-keyed per site so the new colour fades in instead of snapping. */}
      <div key={active} className="lb-wash" aria-hidden="true" />

      {/* Title block */}
      <header className="text-center">
        <div key={active} className="lb-fade font-display-lg uppercase" style={{ fontSize: 22, color: accent, textShadow: `0 0 24px ${accent}66` }}>
          {lb?.title || cfg.title}
        </div>
        <h1 className="font-display-lg uppercase text-white leading-[0.9]" style={{ fontSize: 'clamp(2.8rem, 8vw, 4.6rem)', letterSpacing: '-.01em', textShadow: '0 6px 30px rgba(0,0,0,.6)' }}>
          Leaderboard
        </h1>
        <div className="flex flex-wrap items-center justify-center gap-2 mt-5">
          <button type="button" onClick={copyCode} className="lb-ghost-btn" title="Copy referral code">
            Code: <span style={{ color: accent }}>{cfg.code}</span>
            <span className="material-symbols-outlined" style={{ fontSize: 13 }}>{copied ? 'check' : 'content_copy'}</span>
          </button>
          <a href={cfg.claimUrl} target="_blank" rel="noopener noreferrer" className="lb-ghost-btn">
            Play on {cfg.title}
            <span className="material-symbols-outlined" style={{ fontSize: 13 }}>open_in_new</span>
          </a>
        </div>
      </header>

      {/* Site switcher */}
      <div className="mt-9 text-center font-display-lg uppercase text-[#c9c9d6]" style={{ fontSize: 11, letterSpacing: '.06em' }}>
        Switch between <span style={{ color: accent }}>{SITES.length}</span> leaderboards
      </div>
      <nav className="lb-tabs mt-4 mx-auto flex justify-center" aria-label="Leaderboards">
        {SITES.map((s) => {
          const on = active === s.id;
          const a = PLATFORM_CONFIG[s.id].accent;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => setActive(s.id)}
              aria-pressed={on}
              className={`lb-tab ${on ? 'is-on' : ''}`}
              style={{ '--tab-accent': a }}
            >
              <img alt={PLATFORM_CONFIG[s.id].title} src={s.logo} className="max-h-7 max-w-[120px] object-contain" />
            </button>
          );
        })}
      </nav>
      <p className="mt-4 text-center font-display-lg uppercase text-[#5d5d6c]" style={{ fontSize: 9.5, letterSpacing: '.06em' }}>
        {lb ? (
          <>
            <span style={{ color: accent }}>${Math.round(lb.totalPrizePool).toLocaleString()}</span> prize pool · {cfg.period} race
          </>
        ) : (
          `${cfg.period} race`
        )}
      </p>
      {cfg.note && (
        <p key={`note-${active}`} className="lb-fade text-center font-title-md text-[#a5a5b4] max-w-xl mx-auto mt-2" style={{ fontSize: 12, lineHeight: 1.55 }}>
          {cfg.note}
        </p>
      )}

      {liveError ? (
        <div className="flex items-center justify-center min-h-[420px] text-center text-[#888899] font-mono-brand text-sm">{liveError}</div>
      ) : !lb ? (
        <div className="flex items-center justify-center min-h-[420px] text-center text-[#7d7d8c] font-mono-brand text-sm uppercase tracking-widest">Loading…</div>
      ) : (
        <>
          {/* Podium */}
          <section className="relative mt-16">
            <div className="absolute inset-0 overflow-hidden pointer-events-none hidden md:block" aria-hidden="true">
              <img alt="" src={AK47_ASSET} className="lb-decor" style={{ left: 0, top: 70, width: 150 }} />
              <img alt="" src={LOCK_ASSET} className="lb-decor" style={{ right: 0, top: 30, width: 110, animationDelay: '-2s' }} />
            </div>
            <div className="grid grid-cols-3 gap-2 sm:gap-4 max-w-[640px] mx-auto items-end">
              {[2, 1, 3].map((place) => (
                <PodiumCard key={place} place={place} row={rowAt(place)} prize={prizeAt(place)} cfg={cfg} compact={narrow} />
              ))}
            </div>
          </section>

          <RaceCountdown startsAt={lb.startsAt} endsAt={lb.endsAt} status={lb.status} accent={accent} />

          {/* Challengers */}
          <section className="max-w-[720px] mx-auto">
            <div className="flex items-end justify-between mb-4">
              <h2 className="font-display-lg uppercase text-white" style={{ fontSize: 20 }}>Challengers</h2>
              <label className="flex flex-col items-start gap-1">
                <span className="font-display-lg uppercase text-[#8b8b9c]" style={{ fontSize: 9, letterSpacing: '.06em' }}>Show</span>
                <span className="lb-select">
                  <select value={show} onChange={(e) => setShow(Number(e.target.value))}>
                    {SHOW_OPTIONS.map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                  <span className="material-symbols-outlined" style={{ fontSize: 16 }}>expand_more</span>
                </span>
              </label>
            </div>
            <div className="pb-2">
              <div className="flex flex-col gap-2">
                <div className="grid grid-cols-[26px_1fr_86px_64px] sm:grid-cols-[48px_1fr_160px_120px] gap-2 sm:gap-3 px-3 sm:px-4 pb-1 font-display-lg uppercase text-[#8b8b9c]" style={{ fontSize: 10, letterSpacing: '.06em' }}>
                  <div />
                  <div className="flex items-center gap-1.5">
                    <span className="material-symbols-outlined" style={{ fontSize: 14 }}>account_circle</span>User
                  </div>
                  <div className="flex items-center justify-end gap-1.5">
                    <span className="material-symbols-outlined" style={{ fontSize: 14 }}>toll</span>{cfg.valueLabel}
                  </div>
                  <div className="flex items-center justify-end gap-1.5">
                    <span className="material-symbols-outlined" style={{ fontSize: 14 }}>redeem</span>Prize
                  </div>
                </div>
                {Array.from({ length: show }, (_, i) => i + 4).map((rank) => (
                  <ChallengerRow key={rank} rank={rank} row={rowAt(rank)} prize={prizeAt(rank)} cfg={cfg} />
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      <div
        className="max-w-[720px] mx-auto mt-12 rounded-lg px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left"
        style={{ background: '#16161e', border: '1px solid #22222d', boxShadow: `inset 3px 0 0 ${accent}` }}
      >
        <p className="font-title-md text-sm text-[#d6d6e0] font-semibold">
          Not on the board yet? Sign up on {lb?.title || cfg.title} with code <span style={{ color: accent }}>{cfg.code}</span> and start wagering.
        </p>
        <a href={cfg.claimUrl} target="_blank" rel="noopener noreferrer" className="lb-ghost-btn flex-shrink-0">
          Check it out
          <span className="material-symbols-outlined" style={{ fontSize: 13 }}>arrow_forward</span>
        </a>
      </div>
    </main>
  );
}
